import * as React from 'react';
import Media from 'react-media';

import URI from 'jsuri';

import type UserAgentInfo from 'js/lib/useragent';

import { ArrowNextIcon } from '@coursera/cds-icons';

import DesktopSearchCard from 'bundles/coursera-plus-common/components/search/DesktopSearchCard';
import MobileSearchCard from 'bundles/coursera-plus-common/components/search/MobileSearchCard';
import { adaptSearchDataToLegacyCards } from 'bundles/coursera-plus-common/components/search/utils/adaptSearchDataToLegacyCards';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { SEARCH_MAIN_INDEX_ID, TABLET_SCREEN_WIDTH } from 'bundles/search-common/constants';
import { IS_PART_OF_COURSERA_PLUS } from 'bundles/search-common/constants/filter';
import { useSearch } from 'bundles/search-common/providers/SearchContext';

import _t from 'i18n!nls/coursera-plus';

import 'css!./__styles__/InPageAlgoliaSearchResults';

export type InputProps = {
  userAgent: UserAgentInfo;
  currentSearchQuery: string;
  numberOfResults: number;
  currentTab: string;
  removeLinks?: boolean;
  showCourseraLitePill?: boolean;
  isLoading?: boolean; // Kept for backward compatibility but not used internally
};

type Props = InputProps;

const InPageSearchResults = ({
  userAgent,
  currentSearchQuery,
  removeLinks = false,
  showCourseraLitePill = false,
}: Props) => {
  const { getSearchResults } = useSearch();

  const searchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const { hitsTotalCount = 0, elements: hits, query } = searchData || {};
  const refLink = new URI('/search')
    .addQueryParam('query', currentSearchQuery)
    .addQueryParam(IS_PART_OF_COURSERA_PLUS, true)
    .toString();
  const currentNumberOfHits = hitsTotalCount;

  return (
    <div className="rc-AlgoliaSearchResults">
      <div className="ais-InfiniteHits">
        <ul className="ais-InfiniteHits-list">
          {hits?.map((hit, hitPosition) => (
            <li key={hit.id} className="ais-InfiniteHits-item">
              <Media query={{ maxWidth: TABLET_SCREEN_WIDTH }} defaultMatches={userAgent?.isMobileBrowser}>
                {(matches) => {
                  const SearchCardComponent = matches ? MobileSearchCard : DesktopSearchCard;
                  if (hit.__typename === 'Search_ProductHit') {
                    const adaptedHit = adaptSearchDataToLegacyCards(hit);

                    return (
                      <SearchCardComponent
                        hit={adaptedHit}
                        hitPosition={hitPosition}
                        queryID={query}
                        showCourseraLitePill={showCourseraLitePill}
                        removeLinks={removeLinks}
                      />
                    );
                  }
                  return null;
                }}
              </Media>
            </li>
          ))}
        </ul>
      </div>
      {currentNumberOfHits > 0 && !removeLinks && (
        <TrackedLink2
          data={{ searchQuery: currentSearchQuery }}
          withVisibilityTracking={true}
          requireFullyVisible={true}
          href={refLink}
          target="_blank"
          rel="noopener noreferrer"
          trackingName="view_all_coursera_plus_results_search"
          className="link-wrapper"
        >
          <h3 className="view-all-link">{_t('See more results')}</h3>
          <ArrowNextIcon color="interactive" size="small" style={{ transform: 'translateY(3px)', fontSize: '10px' }} />
        </TrackedLink2>
      )}
    </div>
  );
};

export default InPageSearchResults;
